module AverageRate

open FSharpPlus
open Client
open Feliz.Bulma
open PortalLibs.Client.Util
open ContractPageModel
open Model
open Shared
open Feliz
open PortalLibs.Client
open DisplayNumber

type TotalRate =
  { Cost: float; Hours: float; CostToComplete: float }
  static member Zero = { Cost = 0.0; Hours = 0.0; CostToComplete = 0.0 }
  static member (+) (a: TotalRate, b: TotalRate) =
    { Cost = a.Cost + b.Cost
      Hours = a.Hours + b.Hours
      CostToComplete = a.CostToComplete + b.CostToComplete }
  
let totalColumns remainingHoursEnabled costs =
  let labor, equip, progress =
    costs
    |> List.fold (fun (labor, equip, progress) c ->
      let cost =
        { Cost = c.ActualCostToDate; Hours = c.ActualHoursToDate; CostToComplete = c.CostToComplete }
      (if c.IsLaborRateCost then labor + cost else labor),
      (if c.IsEquipRateCost then equip + cost else equip),
      progress + c.ProgressEntryQuantities)
      (TotalRate.Zero, TotalRate.Zero, 0.0)
  [
    let td (txt: string) =
      Html.td [
        prop.style [style.textAlign.right]
        prop.children [
          Html.strong(txt)
        ]
      ]
    td (progress |> AsNumber.stdOrDef)
    td (labor.Cost / labor.Hours |> AsCurrency.stdOrDef)

    if remainingHoursEnabled then
      td (labor.CostToComplete / (labor.Cost / labor.Hours)
          |> Option.ofInfinite
          |> Option.defaultValue 0.0
          |> sprintf "%.0f")

    td (equip.Cost / equip.Hours |> AsCurrency.stdOrDef)
    
    if remainingHoursEnabled then
      td (equip.CostToComplete / (equip.Cost / equip.Hours)
          |> Option.ofInfinite
          |> Option.defaultValue 0.0
          |> sprintf "%.0f")
  ]

[<ReactComponent>]
let CostsRow remainingHoursEnabled (code: string) (desc: string) costs key =
  Html.tr [
    yield Html.td [ prop.style[style.textAlign.right]; prop.children[Html.strong code] ]
    yield Html.td [ Html.strong desc ]
    yield! totalColumns remainingHoursEnabled costs ]

[<ReactComponent>]
let Totals applicationSettings costsGrouped =
  let allCosts = costsGrouped >>= fun v -> v.Costs

  Html.tr [
    yield Html.td []
    yield Html.td [
            Html.div [
              prop.style [ style.textAlign.right; style.marginRight(length.px 25) ]
              prop.children [
                Html.strong "AVERAGE/TOTAL:"
              ]
            ]
          ]
    yield! totalColumns applicationSettings allCosts ]

let View =
  React.memo (
    "AverageRateView",
    (fun
      (props:
        {| JobCosts: Map<ContractId, JobPhaseCosts list>
           RemainingHoursEnabled: bool
           Contract: Contract
           Dispatch: Msg -> unit |}) ->
      
      let costView remainingHoursEnabled costsGrouped =
        Html.div [
          prop.style [
            style.paddingBottom (length.px 50); style.display.flex; style.justifyContent.spaceAround
          ]
          prop.children [
            Bulma.table [
              table.isHoverable
              prop.style [style.minWidth(length.px 500); Styles.stickyTableBorder]
              prop.children [
                Html.thead [
                  prop.style (Styles.sticky())
                  prop.children [
                    if props.RemainingHoursEnabled then
                      Html.tr [
                        Html.th [
                          prop.colSpan 3
                        ]
                        Html.th [
                          prop.colSpan 2
                          prop.style[style.backgroundColor.red
                                     style.backgroundColor "rgb(235, 235, 235)"; style.textAlign.center]
                          prop.text "Labor"
                        ]
                        Html.th [
                          prop.colSpan 2
                          prop.style[style.backgroundColor.red
                                     style.backgroundColor "rgb(239, 239, 227)"; style.textAlign.center]
                          prop.text "Equipment"
                        ]
                      ]
                    Html.tr [
                      prop.children [
                        let th styles (txt: string) (width: int option) =
                          Html.th [
                            prop.style[style.verticalAlign.middle
                                       if width.IsSome then style.width(length.px(width.Value))
                                       yield! styles]
                            prop.text txt
                          ]
                          
                        th [style.textAlign.right] "Phase Code" (Some 150)
                        th [] "Description" None
                        th [style.textAlign.right] "Progress Entry Quantities" (Some 200) 
                        th [style.textAlign.right] (if remainingHoursEnabled then "Rate" else "Labor Rate") (Some 150)
                        if remainingHoursEnabled then
                          th [style.textAlign.right] "Remaining Hours" (Some 200)
                        th [style.textAlign.right] (if remainingHoursEnabled then "Rate" else "Equip Rate") (Some 150)
                        if remainingHoursEnabled then
                          th [style.textAlign.right] "Remaining Hours" (Some 200)
                      ]
                    ]
                  ]
                  
                ]
                Html.tbody [
                  match
                    costsGrouped
                    |>> (fun v ->
                      { Phase = v.Phase
                        Costs = v.Costs |> List.filter (fun x -> x.IsLaborRateCost || x.IsEquipRateCost) })
                    |> List.filter (fun v -> not v.Costs.IsEmpty)
                  with
                  | [] ->
                    yield
                      Html.tr [
                        Html.td [
                          prop.colSpan(8)
                          prop.style [style.textAlign.center; style.padding(30, 0)]
                          prop.text "No Job costs available."
                        ]]
                  | costs ->
                    yield!
                      costs
                      |>> (fun v ->
                        CostsRow
                          props.RemainingHoursEnabled
                          v.Phase.Code
                          v.Phase.Desc
                          v.Costs
                          $"Costs-%s{v.Phase.Code}" )

                    if costs.Length > 1 then
                      yield Totals props.RemainingHoursEnabled costs ]
              ]
            ]
          ]
        ]

      loadingMessageOrContent
        "Loading Rates..."
        (costView props.RemainingHoursEnabled)
        (Map.tryFind props.Contract.Id props.JobCosts))
  )